/**
 * ModelSelect — catalog-native unified model selector.
 *
 * Replaces the legacy Inline/Compact/Enhanced/Modal four-state ModelSelector
 * families with a single component that reads CatalogModel directly (no
 * legacy adapter flattening). Composes model-display primitives for rendering;
 * never branches on modality/billing inline.
 *
 * Long lists are tamed by three affordances: a bounded-height scroll body,
 * base-model identity rows with expandable routing channels, and tag/vendor
 * filters on top of text search. All chrome is i18n-keyed.
 *
 * Variants:
 *  - 'inline'  (default): full listbox, always visible
 *  - 'compact':           denser listbox, always visible
 *  - 'modal':             trigger button opens a dialog list
 */

import { useEffect, useMemo, useState, useId } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { ChevronDown, ChevronRight, X } from 'lucide-react'
import { Badge } from '@/components/ui'
import { cn } from '@/lib/cn'
import { useI18n } from '@/i18n'
import { useCatalogModels } from '@/lib/queries/useCatalogModels'
import { useModelCatalog } from '@/lib/queries/useModelsQuery'
import {
  baseModelName,
  displayName,
  hostCategory,
  modelId,
  organization,
  routingTier,
  sameModelId,
  supportsFunctionCalling,
  supportsReasoning,
  supportsVision,
} from '@/lib/models/catalogAccessors'
import { ModelBadge } from './ModelBadge'
import { BillingLabel } from './BillingLabel'
import { ModelCapabilitySummary } from './ModelCapabilitySummary'
import { SourceHealthBanner, type SourceStatus } from './SourceHealthBanner'
import { CapabilityWarningBadge } from './CapabilityWarningBadge'
import type { CatalogModel, Modality, ModelCatalogResponse } from '@/types'

type ImageOperation = 'generate' | 'edit' | 'fuse'
type SelectableCapability = 'vision' | 'reasoning' | 'function_calling'

const CAPABILITY_OPTIONS: readonly SelectableCapability[] = [
  'function_calling',
  'vision',
  'reasoning',
]

// Channel ordering within one base-model identity; any other channel follows.
const TIER_ORDER = ['official', 'stable', 'dev', 'computorail', 'bltcy']
// Above this identity-row count, multi-channel rows start collapsed so a 200+
// model catalog remains a compact list of base models.
const COLLAPSE_THRESHOLD = 40

// ---------------------------------------------------------------------------
// cva variants (typed via VariantProps — DS rule §3)
// ---------------------------------------------------------------------------

const modelSelectVariants = cva(
  'relative w-full border border-border bg-card text-foreground rounded-lg overflow-hidden',
  {
    variants: {
      variant: {
        inline: 'shadow-elevation-1',
        compact: '',
        modal: '',
      },
    },
    defaultVariants: { variant: 'inline' },
  }
)

const optionVariants = cva(
  'flex flex-col gap-1 cursor-pointer select-none rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
  {
    variants: {
      variant: {
        inline: 'px-3 py-3',
        compact: 'px-2 py-1.5',
        modal: 'px-3 py-3',
      },
      selected: {
        true: 'bg-primary/10 text-primary',
        false: 'hover:bg-muted',
      },
    },
    defaultVariants: { variant: 'inline', selected: false },
  }
)

const identityRowVariants = cva(
  'w-full flex flex-col gap-1.5 cursor-pointer select-none text-left transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
  {
    variants: {
      variant: {
        inline: 'px-3 py-3',
        compact: 'px-2 py-2',
        modal: 'px-3 py-3',
      },
      selected: {
        true: 'bg-primary/10 text-primary',
        false: 'hover:bg-muted text-foreground',
      },
    },
    defaultVariants: { variant: 'inline', selected: false },
  }
)

const channelRowVariants = cva(
  'flex flex-col gap-1 cursor-pointer select-none border-l-2 border-border transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
  {
    variants: {
      variant: {
        inline: 'ml-4 px-3 py-2',
        compact: 'ml-3 px-2 py-1.5',
        modal: 'ml-4 px-3 py-2',
      },
      selected: {
        true: 'bg-primary/10 border-l-primary text-primary',
        false: 'hover:bg-muted',
      },
    },
    defaultVariants: { variant: 'inline', selected: false },
  }
)

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface ModelSelectProps extends VariantProps<typeof modelSelectVariants> {
  value: string
  onChange: (modelId: string) => void
  modality?: Modality
  tier?: string
  operationFilter?: ImageOperation
  /**
   * Further narrows text-modality results by capability sub-filter.
   * Only meaningful when modality='text'. Uses supportsVision / supportsReasoning
   * / supportsFunctionCalling accessors so operators cannot pick a
   * capability-incapable model into a slot that requires that capability.
   */
  capabilityFilter?: 'vision' | 'reasoning' | 'function_calling'
  /** Allow clearing the selection (selecting no model). */
  allowEmpty?: boolean
  /** Label shown in the trigger and as the empty option row when allowEmpty is true. */
  emptyLabel?: string
  /**
   * When vendor/tag/search filters hide the current value, auto-select the first
   * remaining identity (image playground). Default false preserves text-slot
   * behavior where filters never clobber a stored selection.
   */
  syncSelectionToFilters?: boolean
  disabled?: boolean
  className?: string
}

// ---------------------------------------------------------------------------
// Grouping helpers
// ---------------------------------------------------------------------------

interface ModelIdentity {
  key: string
  models: CatalogModel[]
}

type CatalogWithTrustSignals = ModelCatalogResponse & {
  sources?: SourceStatus[]
}

function capabilityInconsistent(model: CatalogModel): boolean {
  return (
    'capability_inconsistent' in model && model.capability_inconsistent === true
  )
}

function capabilityConflictFlags(model: CatalogModel): string[] {
  const flags = model.capability_conflict_flags
  return Array.isArray(flags)
    ? flags.filter((f): f is string => typeof f === 'string' && f.length > 0)
    : []
}

function channelRank(model: CatalogModel): number {
  const idTier = modelId(model).split('/')[0]
  const channelTier = TIER_ORDER.includes(idTier) ? idTier : routingTier(model)
  const rank = TIER_ORDER.indexOf(channelTier)
  return rank === -1 ? TIER_ORDER.length : rank
}

function groupModels(models: CatalogModel[]): ModelIdentity[] {
  const buckets = new Map<string, CatalogModel[]>()
  for (const m of models) {
    const key = baseModelName(m)
    const arr = buckets.get(key)
    if (arr) arr.push(m)
    else buckets.set(key, [m])
  }
  return Array.from(buckets.entries()).map(([key, groupedModels]) => ({
    key,
    models: [...groupedModels].sort(
      (a, b) => channelRank(a) - channelRank(b) || modelId(a).localeCompare(modelId(b)),
    ),
  }))
}

function displayNameWithoutTier(model: CatalogModel): string {
  const id = modelId(model)
  const name = displayName(model)
  const baseName = baseModelName(model)
  if (name === id) return baseName
  if (baseName === id) return name

  const tierPrefix = `${id.split('/')[0]}/`
  return name.startsWith(tierPrefix) ? name.slice(tierPrefix.length) : name
}

function identityDisplayName(identity: ModelIdentity): string {
  return displayNameWithoutTier(identity.models[0])
}

function cheapestPerTokenChannelId(models: CatalogModel[]): string | null {
  const comparableChannels: Array<{ id: string; outputPrice: number }> = []

  for (const model of models) {
    if (
      model.billing.billing_mode === 'per_token' &&
      model.billing.output_per_million !== null
    ) {
      comparableChannels.push({
        id: modelId(model),
        outputPrice: model.billing.output_per_million,
      })
    }
  }

  if (comparableChannels.length < 2) return null

  const lowestPrice = Math.min(...comparableChannels.map((channel) => channel.outputPrice))
  const lowestChannels = comparableChannels.filter(
    (channel) => channel.outputPrice === lowestPrice,
  )
  return lowestChannels.length === 1 ? lowestChannels[0].id : null
}

// ---------------------------------------------------------------------------
// Search box sub-component
// ---------------------------------------------------------------------------

interface SearchBoxProps {
  value: string
  onChange: (v: string) => void
  placeholder: string
  className?: string
}

function SearchBox({ value, onChange, placeholder, className }: SearchBoxProps) {
  return (
    <div className={cn('px-2 py-2', className)}>
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        className={cn(
          'w-full rounded-md border border-input bg-card text-foreground text-sm px-2 py-1',
          'placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-ring',
        )}
      />
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tag filter chips
// ---------------------------------------------------------------------------

interface TagFilterProps<T extends string> {
  tags: readonly T[]
  selected: ReadonlySet<T>
  onToggle: (tag: T) => void
  onClear: () => void
  label: string
  clearLabel: string
  getTagLabel?: (tag: T) => string
}

function TagFilter<T extends string>({
  tags,
  selected,
  onToggle,
  onClear,
  label,
  clearLabel,
  getTagLabel,
}: TagFilterProps<T>) {
  if (tags.length === 0) return null
  return (
    <div className="px-2 py-2 flex flex-wrap items-center gap-1.5">
      <span className="text-xs text-muted-foreground mr-0.5">{label}</span>
      {tags.map((tag) => {
        const active = selected.has(tag)
        return (
          <button
            key={tag}
            type="button"
            aria-pressed={active}
            onClick={() => onToggle(tag)}
            className={cn(
              'text-xs rounded-full px-2 py-0.5 border transition-colors focus:outline-none focus:ring-2 focus:ring-ring',
              active
                ? 'bg-primary text-primary-foreground border-primary'
                : 'bg-muted text-muted-foreground border-border hover:bg-accent',
            )}
          >
            {getTagLabel ? getTagLabel(tag) : tag}
          </button>
        )
      })}
      {selected.size > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="text-xs text-muted-foreground hover:text-foreground underline focus:outline-none focus:ring-2 focus:ring-ring rounded"
        >
          {clearLabel}
        </button>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Empty option row (when allowEmpty=true)
// ---------------------------------------------------------------------------

interface EmptyOptionRowProps {
  isSelected: boolean
  variant: 'inline' | 'compact' | 'modal'
  emptyLabel: string
  onSelect: () => void
}

function EmptyOptionRow({ isSelected, variant, emptyLabel, onSelect }: EmptyOptionRowProps) {
  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-label="clear selection"
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onSelect()
        }
      }}
      className={cn(optionVariants({ variant, selected: isSelected }))}
    >
      <span className={cn('text-sm italic', isSelected ? 'text-primary' : 'text-muted-foreground')}>
        {emptyLabel}
      </span>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Single-channel identity row
// ---------------------------------------------------------------------------

interface SingleIdentityRowProps {
  model: CatalogModel
  name: string
  isSelected: boolean
  variant: 'inline' | 'compact' | 'modal'
  showCapability: boolean
  onClick: () => void
}

function SingleIdentityRow({
  model,
  name,
  isSelected,
  variant,
  showCapability,
  onClick,
}: SingleIdentityRowProps) {
  const id = modelId(model)

  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-label={id}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={cn(optionVariants({ variant, selected: isSelected }))}
    >
      <div className="flex items-center justify-between gap-2 min-w-0">
        <span className={cn('font-medium text-sm truncate', isSelected ? 'text-primary' : 'text-foreground')}>
          {name}
        </span>
        <BillingLabel billing={model.billing} className="shrink-0 text-xs" />
      </div>
      <span className="inline-flex items-center gap-1.5 flex-wrap">
        <ModelBadge model={model} />
        <CapabilityWarningBadge
          inconsistent={capabilityInconsistent(model)}
          conflictFlags={capabilityConflictFlags(model)}
        />
      </span>
      {showCapability && isSelected && (
        <ModelCapabilitySummary capability={model.capability} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Expanded channel row
// ---------------------------------------------------------------------------

interface ChannelRowProps {
  model: CatalogModel
  identityName: string
  isSelected: boolean
  isCheapest: boolean
  variant: 'inline' | 'compact' | 'modal'
  showCapability: boolean
  cheapestLabel: string
  onClick: () => void
}

function ChannelRow({
  model,
  identityName,
  isSelected,
  isCheapest,
  variant,
  showCapability,
  cheapestLabel,
  onClick,
}: ChannelRowProps) {
  const id = modelId(model)
  const channelName = displayNameWithoutTier(model)
  const showChannelName = channelName !== identityName

  return (
    <div
      role="option"
      aria-selected={isSelected}
      aria-label={id}
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onClick()
        }
      }}
      className={cn(channelRowVariants({ variant, selected: isSelected }))}
    >
      <div className="flex items-start justify-between gap-2 min-w-0">
        <div className="flex flex-col gap-1 min-w-0">
          <span className="inline-flex items-center gap-1.5 flex-wrap">
            <ModelBadge model={model} />
            <CapabilityWarningBadge
              inconsistent={capabilityInconsistent(model)}
              conflictFlags={capabilityConflictFlags(model)}
            />
          </span>
          {showChannelName && (
            <span className={cn('text-xs truncate', isSelected ? 'text-primary' : 'text-muted-foreground')}>
              {channelName}
            </span>
          )}
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <BillingLabel billing={model.billing} className="text-xs" />
          {isCheapest && (
            <Badge variant="success" size="sm" className="px-1.5 py-0 text-[10px] leading-4">
              {cheapestLabel}
            </Badge>
          )}
        </div>
      </div>
      {showCapability && isSelected && (
        <ModelCapabilitySummary capability={model.capability} />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Identity option list
// ---------------------------------------------------------------------------

interface OptionListProps {
  identities: ModelIdentity[]
  value: string
  variant: 'inline' | 'compact' | 'modal'
  onSelect: (id: string) => void
  isIdentityOpen: (key: string) => boolean
  onToggleIdentity: (key: string) => void
  noModelsLabel: string
  channelsLabel: string
  expandChannelsLabel: string
  collapseChannelsLabel: string
  channelCountLabel: (count: number) => string
  cheapestChannelLabel: string
  allowEmpty?: boolean
  emptyLabel: string
}

function OptionList({
  identities,
  value,
  variant,
  onSelect,
  isIdentityOpen,
  onToggleIdentity,
  noModelsLabel,
  channelsLabel,
  expandChannelsLabel,
  collapseChannelsLabel,
  channelCountLabel,
  cheapestChannelLabel,
  allowEmpty,
  emptyLabel,
}: OptionListProps) {
  return (
    <div role="listbox" className="flex flex-col">
      {allowEmpty && (
        <div className="divide-y divide-border">
          <EmptyOptionRow
            isSelected={value === ''}
            variant={variant}
            emptyLabel={emptyLabel}
            onSelect={() => onSelect('')}
          />
        </div>
      )}
      {identities.length === 0 && (
        <div className="px-3 py-4 text-sm text-muted-foreground text-center">
          {noModelsLabel}
        </div>
      )}
      {identities.map((identity) => {
        const name = identityDisplayName(identity)
        const selectedChannel = identity.models.find((model) => sameModelId(value, modelId(model)))
        const cheapestChannelId = cheapestPerTokenChannelId(identity.models)

        if (identity.models.length === 1) {
          const model = identity.models[0]
          const id = modelId(model)
          return (
            <SingleIdentityRow
              key={identity.key}
              model={model}
              name={name}
              isSelected={sameModelId(value, id)}
              variant={variant}
              showCapability={variant !== 'compact'}
              onClick={() => onSelect(id)}
            />
          )
        }

        const open = isIdentityOpen(identity.key)
        const isSelected = selectedChannel !== undefined
        return (
          <div key={identity.key}>
            <button
              type="button"
              aria-expanded={open}
              aria-label={`${name} · ${open ? collapseChannelsLabel : expandChannelsLabel}`}
              onClick={() => onToggleIdentity(identity.key)}
              className={cn(identityRowVariants({ variant, selected: isSelected }))}
            >
              <span className="flex items-center gap-2 min-w-0">
                {open ? (
                  <ChevronDown size={14} className="shrink-0 text-muted-foreground" aria-hidden />
                ) : (
                  <ChevronRight size={14} className="shrink-0 text-muted-foreground" aria-hidden />
                )}
                <span className="font-medium text-sm truncate">{name}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {channelCountLabel(identity.models.length)}
                </span>
              </span>
              {selectedChannel && (
                <span className="flex items-center justify-between gap-2 min-w-0 pl-6">
                  <span className="inline-flex items-center gap-1.5 flex-wrap">
                    <ModelBadge model={selectedChannel} />
                    <CapabilityWarningBadge
                      inconsistent={capabilityInconsistent(selectedChannel)}
                      conflictFlags={capabilityConflictFlags(selectedChannel)}
                    />
                  </span>
                  <BillingLabel billing={selectedChannel.billing} className="shrink-0 text-xs" />
                </span>
              )}
            </button>
            {open && (
              <div
                role="group"
                aria-label={`${name} ${channelsLabel}`}
                className="flex flex-col divide-y divide-border"
              >
                {identity.models.map((model) => {
                  const id = modelId(model)
                  return (
                    <ChannelRow
                      key={id}
                      model={model}
                      identityName={name}
                      isSelected={sameModelId(value, id)}
                      isCheapest={cheapestChannelId === id}
                      variant={variant}
                      showCapability={variant !== 'compact'}
                      cheapestLabel={cheapestChannelLabel}
                      onClick={() => onSelect(id)}
                    />
                  )
                })}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Selected summary (the "locked" view shared by all variants)
// ---------------------------------------------------------------------------

interface SelectedSummaryProps {
  model: CatalogModel | undefined
  isEmptySelected: boolean
  ariaLabel: string
  emptyLabel: string
  selectLabel: string
  reselectLabel: string
  disabled?: boolean
  open: boolean
  ariaHasPopup: 'dialog' | 'listbox'
  onOpen: () => void
  className?: string
}

function SelectedSummary({
  model,
  isEmptySelected,
  ariaLabel,
  emptyLabel,
  selectLabel,
  reselectLabel,
  disabled,
  open,
  ariaHasPopup,
  onOpen,
  className,
}: SelectedSummaryProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onOpen}
      aria-label={ariaLabel}
      aria-haspopup={ariaHasPopup}
      aria-expanded={open}
      className={cn(
        'w-full flex items-center gap-3 border border-border bg-card text-foreground rounded-lg px-3 py-2.5 text-sm text-left transition-colors',
        'hover:bg-muted focus:outline-none focus:ring-2 focus:ring-ring',
        'disabled:opacity-60 disabled:cursor-not-allowed',
        className,
      )}
    >
      {model ? (
        <span className="flex-1 min-w-0 flex flex-col gap-1">
          <span className="flex items-center justify-between gap-2 min-w-0">
            <span className="font-medium truncate">{displayNameWithoutTier(model)}</span>
            <BillingLabel billing={model.billing} className="shrink-0 text-xs" />
          </span>
          <ModelBadge model={model} />
        </span>
      ) : isEmptySelected ? (
        <span className="flex-1 text-muted-foreground italic">{emptyLabel}</span>
      ) : (
        <span className="flex-1 text-muted-foreground">{selectLabel}</span>
      )}
      {(model || isEmptySelected) && (
        <span className="shrink-0 text-xs text-primary font-medium">{reselectLabel}</span>
      )}
      <ChevronDown size={14} className="text-muted-foreground shrink-0" aria-hidden />
    </button>
  )
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function ModelSelect({
  value,
  onChange,
  modality,
  tier,
  operationFilter,
  capabilityFilter,
  allowEmpty = false,
  emptyLabel,
  syncSelectionToFilters = false,
  disabled = false,
  variant = 'inline',
  className,
}: ModelSelectProps) {
  const { t } = useI18n()
  // Picker open by default only for an inline required-but-missing selection.
  const [open, setOpen] = useState(() => variant !== 'modal' && value === '' && !allowEmpty && !disabled)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedTags, setSelectedTags] = useState<Set<string>>(new Set())
  const [selectedVendors, setSelectedVendors] = useState<Set<string>>(new Set())
  const [selectedHosts, setSelectedHosts] = useState<Set<string>>(new Set())
  const [selectedCapabilities, setSelectedCapabilities] = useState<Set<SelectableCapability>>(new Set())
  // Explicit per-identity open/closed overrides (true=open). Absent → default rule.
  const [identityOverride, setIdentityOverride] = useState<Record<string, boolean>>({})
  const labelId = useId()

  const resolvedEmptyLabel = emptyLabel ?? t('modelSelect.useSystemDefault')

  const { data: models = [], isLoading, error } = useCatalogModels(modality)
  const { data: catalog } = useModelCatalog()
  const sources = (catalog as CatalogWithTrustSignals | undefined)?.sources

  const afterTier = tier ? models.filter((m) => routingTier(m) === tier) : models

  const afterOpFilter = operationFilter
    ? afterTier.filter((m) => {
        if (m.capability.modality !== 'image') return false
        return m.capability.operations.includes(operationFilter)
      })
    : afterTier

  const afterCapFilter = capabilityFilter
    ? afterOpFilter.filter((m) => {
        if (capabilityFilter === 'vision') return supportsVision(m)
        if (capabilityFilter === 'reasoning') return supportsReasoning(m)
        return supportsFunctionCalling(m)
      })
    : afterOpFilter

  const allVendors = useMemo(() => {
    const set = new Set<string>()
    for (const m of afterCapFilter) {
      const org = organization(m)
      if (org) set.add(org)
    }
    return Array.from(set).sort()
  }, [afterCapFilter])

  const afterVendors = selectedVendors.size
    ? afterCapFilter.filter((m) => selectedVendors.has(organization(m)))
    : afterCapFilter

  const allHosts = useMemo(() => {
    const set = new Set<string>()
    for (const m of afterVendors) {
      const host = hostCategory(m)
      if (host) set.add(host)
    }
    return Array.from(set).sort()
  }, [afterVendors])

  const afterHosts = selectedHosts.size
    ? afterVendors.filter((m) => {
        const host = hostCategory(m)
        return !!host && selectedHosts.has(host)
      })
    : afterVendors

  const allTags = useMemo(() => {
    const set = new Set<string>()
    for (const m of afterCapFilter) for (const tag of m.tags ?? []) set.add(tag)
    return Array.from(set).sort()
  }, [afterCapFilter])

  const afterTags = selectedTags.size
    ? afterHosts.filter((m) => {
        const tags = new Set(m.tags ?? [])
        for (const want of selectedTags) if (!tags.has(want)) return false
        return true
      })
    : afterHosts

  const afterCaps = selectedCapabilities.size
    ? afterTags.filter((m) =>
        [...selectedCapabilities].every((cap) =>
          cap === 'vision'
            ? supportsVision(m)
            : cap === 'reasoning'
              ? supportsReasoning(m)
              : supportsFunctionCalling(m),
        ),
      )
    : afterTags

  const allIdentities = groupModels(afterCapFilter)
  const candidateIdentities = groupModels(afterCaps)
  const q = searchQuery.trim().toLowerCase()
  const identities = q
    ? candidateIdentities.filter(
        (identity) =>
          identity.key.toLowerCase().includes(q) ||
          identityDisplayName(identity).toLowerCase().includes(q) ||
          identity.models.some(
            (model) =>
              modelId(model).toLowerCase().includes(q) ||
              displayName(model).toLowerCase().includes(q),
          ),
      )
    : candidateIdentities

  const filterActive =
    q.length > 0 ||
    selectedTags.size > 0 ||
    selectedVendors.size > 0 ||
    selectedHosts.size > 0 ||
    selectedCapabilities.size > 0
  const selectedIdentity = value
    ? identities.find((identity) =>
        identity.models.some((model) => sameModelId(modelId(model), value)),
      )
    : undefined
  const autoCollapse = !filterActive && identities.length > COLLAPSE_THRESHOLD
  // Preserve the old long-list affordance at identity granularity: the selected
  // identity opens first, otherwise the first expandable identity stays open.
  const defaultOpenKey = selectedIdentity?.key ?? identities.find((identity) => identity.models.length > 1)?.key

  // Image playground: when filters hide the current selection, move selection to
  // the first remaining model so ImageConfigPanel matches the visible list.
  useEffect(() => {
    if (!syncSelectionToFilters || disabled || !filterActive) return
    if (identities.length === 0) return
    const stillVisible =
      !!value &&
      identities.some((identity) =>
        identity.models.some((model) => sameModelId(modelId(model), value)),
      )
    if (stillVisible) return
    const first = identities[0]?.models[0]
    if (!first) return
    const nextId = modelId(first)
    if (sameModelId(nextId, value)) return
    onChange(nextId)
  }, [
    syncSelectionToFilters,
    disabled,
    filterActive,
    identities,
    value,
    onChange,
  ])

  function isIdentityOpen(key: string): boolean {
    if (filterActive) return true
    if (key in identityOverride) return identityOverride[key]
    return !autoCollapse || key === defaultOpenKey
  }

  function toggleIdentity(key: string) {
    setIdentityOverride((prev) => ({ ...prev, [key]: !isIdentityOpen(key) }))
  }

  function toggleTag(tag: string) {
    setSelectedTags((prev) => {
      const next = new Set(prev)
      if (next.has(tag)) next.delete(tag)
      else next.add(tag)
      return next
    })
  }

  function toggleVendor(vendor: string) {
    setSelectedVendors((prev) => {
      const next = new Set(prev)
      if (next.has(vendor)) next.delete(vendor)
      else next.add(vendor)
      return next
    })
  }

  function toggleHost(host: string) {
    setSelectedHosts((prev) => {
      const next = new Set(prev)
      if (next.has(host)) next.delete(host)
      else next.add(host)
      return next
    })
  }

  function toggleCapability(capability: SelectableCapability) {
    setSelectedCapabilities((prev) => {
      const next = new Set(prev)
      if (next.has(capability)) next.delete(capability)
      else next.add(capability)
      return next
    })
  }

  // Resolve the locked summary against the full modality base, NOT a narrowed
  // set — search/tag/vendor AND the capability/operation facets only constrain
  // the pickable list; they must never hide the actual stored selection (a
  // reasoning slot storing a non-reasoning model must still show it, not read as
  // unselected). sameModelId aligns a stored value past a missing/legacy tier prefix.
  const selectedModel = models.find((m) => sameModelId(modelId(m), value))

  function handleSelect(id: string) {
    if (!disabled) {
      onChange(id)
      setSearchQuery('')
      setOpen(false)
    }
  }

  const listBody = (
    <OptionList
      identities={identities}
      value={value}
      variant={variant === 'modal' ? 'modal' : (variant ?? 'inline')}
      onSelect={handleSelect}
      isIdentityOpen={isIdentityOpen}
      onToggleIdentity={toggleIdentity}
      noModelsLabel={t('modelSelect.noModels')}
      channelsLabel={t('modelSelect.channels')}
      expandChannelsLabel={t('modelSelect.expandChannels')}
      collapseChannelsLabel={t('modelSelect.collapseChannels')}
      channelCountLabel={(count) => t('modelSelect.channelCount', { count: String(count) })}
      cheapestChannelLabel={t('modelSelect.cheapestChannel')}
      allowEmpty={allowEmpty}
      emptyLabel={resolvedEmptyLabel}
    />
  )

  const controls = (
    <>
      <SourceHealthBanner sources={sources} />
      {allVendors.length > 1 && (
        <TagFilter
          tags={allVendors}
          selected={selectedVendors}
          onToggle={toggleVendor}
          onClear={() => setSelectedVendors(new Set())}
          label={t('modelSelect.vendorFilter')}
          clearLabel={t('modelSelect.clearFilters')}
        />
      )}
      {allHosts.length > 0 && (
        <TagFilter
          tags={allHosts}
          selected={selectedHosts}
          onToggle={toggleHost}
          onClear={() => setSelectedHosts(new Set())}
          label={t('modelSelect.hostFilter')}
          clearLabel={t('modelSelect.clearFilters')}
        />
      )}
      {allTags.length > 0 && (
        <TagFilter
          tags={allTags}
          selected={selectedTags}
          onToggle={toggleTag}
          onClear={() => setSelectedTags(new Set())}
          label={t('modelSelect.tagsFilter')}
          clearLabel={t('modelSelect.clearFilters')}
        />
      )}
      {modality === 'text' && (
        <TagFilter
          tags={CAPABILITY_OPTIONS}
          selected={selectedCapabilities}
          onToggle={toggleCapability}
          onClear={() => setSelectedCapabilities(new Set())}
          label={t('modelSelect.capabilitiesFilter')}
          clearLabel={t('modelSelect.clearFilters')}
          getTagLabel={(capability) =>
            capability === 'function_calling'
              ? t('modelSelect.capabilities.functionCalling')
              : capability === 'vision'
                ? t('modelSelect.capabilities.vision')
                : t('modelSelect.capabilities.reasoning')
          }
        />
      )}
      <SearchBox value={searchQuery} onChange={setSearchQuery} placeholder={t('modelSelect.searchPlaceholder')} />
      {filterActive && (
        <div className="px-3 pb-1 text-xs text-muted-foreground">
          {t('modelSelect.countShown', {
            shown: String(identities.length),
            total: String(allIdentities.length),
          })}
        </div>
      )}
    </>
  )

  // Modal variant: trigger + dialog overlay
  if (variant === 'modal') {
    // Loading/error: render a disabled trigger button so the modal entry-point is always visible.
    if (isLoading || error) {
      return (
        <button
          type="button"
          disabled
          aria-label={t('modelSelect.selectTitle')}
          aria-haspopup="dialog"
          className={cn(
            'inline-flex items-center gap-2 border border-border bg-card text-foreground',
            'rounded-lg px-3 py-2 text-sm',
            'opacity-60 cursor-not-allowed',
            className,
          )}
        >
          <span className="text-muted-foreground">
            {isLoading ? t('modelSelect.loading') : t('modelSelect.loadFailed')}
          </span>
          <ChevronDown size={14} className="text-muted-foreground ml-auto" aria-hidden />
        </button>
      )
    }

    return (
      <>
        <SelectedSummary
          model={selectedModel}
          isEmptySelected={allowEmpty && value === ''}
          ariaLabel={t('modelSelect.selectTitle')}
          emptyLabel={resolvedEmptyLabel}
          selectLabel={t('modelSelect.selectModel')}
          reselectLabel={t('modelSelect.reselect')}
          disabled={disabled}
          open={open}
          ariaHasPopup="dialog"
          onOpen={() => setOpen(true)}
          className={className}
        />

        {open && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby={labelId}
            className="fixed inset-0 z-50 flex items-center justify-center bg-foreground/20"
            onClick={(e) => {
              if (e.target === e.currentTarget) { setOpen(false); setSearchQuery('') }
            }}
          >
            <div
              className={cn(
                'bg-card border border-border rounded-xl shadow-elevation-3',
                'w-full max-w-lg max-h-[70vh] flex flex-col overflow-hidden',
              )}
            >
              <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
                <span id={labelId} className="font-semibold text-foreground">{t('modelSelect.selectTitle')}</span>
                <button
                  type="button"
                  onClick={() => { setOpen(false); setSearchQuery('') }}
                  aria-label={t('modelSelect.closeLabel')}
                  className="text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded"
                >
                  <X size={16} aria-hidden />
                </button>
              </div>
              <div className="border-b border-border shrink-0">
                {controls}
              </div>
              <div className="overflow-y-auto flex-1">
                {listBody}
              </div>
            </div>
          </div>
        )}
      </>
    )
  }

  // Inline / compact variants: loading / error inline box
  if (isLoading) {
    return (
      <div
        className={cn(
          'border border-border bg-card text-foreground rounded-lg px-3 py-3',
          className,
        )}
      >
        <span className="text-sm text-muted-foreground">{t('modelSelect.loading')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div
        className={cn(
          'border border-border bg-card text-foreground rounded-lg px-3 py-3',
          className,
        )}
      >
        <span className="text-sm text-destructive">{t('modelSelect.loadFailed')}</span>
      </div>
    )
  }

  // Inline / compact variants: collapsed locked summary, or the expanded picker.
  if (!open) {
    return (
      <SelectedSummary
        model={selectedModel}
        isEmptySelected={allowEmpty && value === ''}
        ariaLabel={t('modelSelect.selectTitle')}
        emptyLabel={resolvedEmptyLabel}
        selectLabel={t('modelSelect.selectModel')}
        reselectLabel={t('modelSelect.reselect')}
        disabled={disabled}
        open={open}
        ariaHasPopup="listbox"
        onOpen={() => setOpen(true)}
        className={className}
      />
    )
  }

  return (
    <div
      className={cn(modelSelectVariants({ variant }), disabled && 'opacity-60 pointer-events-none', className)}
      aria-disabled={disabled}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-border">
        <span className="font-semibold text-sm text-foreground">{t('modelSelect.selectTitle')}</span>
        <button
          type="button"
          onClick={() => { setOpen(false); setSearchQuery('') }}
          aria-label={t('modelSelect.closeLabel')}
          className="text-muted-foreground hover:text-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring rounded"
        >
          <X size={16} aria-hidden />
        </button>
      </div>
      <div className="border-b border-border">
        {controls}
      </div>
      <div className={cn(variant === 'compact' ? 'max-h-72' : 'max-h-96', 'overflow-y-auto')}>
        {listBody}
      </div>
    </div>
  )
}
